import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { supabase } from "../lib/supabase";
import { formatDateDMY } from "../lib/ScheduleUtils";
import { Card, CardHeader, CardTitle, CardContent } from "./common/Card";
import Button from "./common/Button";
import { Input } from "./common/Input";
import { Label } from "./common/Label";
import {
  Search,
  Building2,
  ArrowLeft,
  Phone,
  Mail,
  Users,
  Loader2,
  MapPin,
  Clock,
  MessageSquare,
} from "lucide-react";
import Spinner from "./common/Spinner";
import { getOrganizationDisplayName } from "../utils/utils";
import useBrandTranslation from "../hooks/useBrandTranslation";

export default function AssociationSearch() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Verificar que el usuario sea un profesional
    if (!user || user.role !== "doctor") {
      navigate("/");
      return;
    }

    // Cargar todas las asociaciones al inicio
    loadAssociations("");
  }, [user, navigate]);

  const loadAssociations = async (search = "") => {
    try {
      setLoading(true);
      setError("");

      let query = supabase
        .from("profiles")
        .select("*")
        .eq("role", "association")
        .is("deleted_at", null);

      // Si hay un término de búsqueda, filtrar por nombre o hospital_name
      if (search.trim()) {
        const searchLower = search.trim();
        query = query.or(
          `hospital_name.ilike.%${searchLower}%,name.ilike.%${searchLower}%,last_name.ilike.%${searchLower}%`
        );
      }

      const { data, error: queryError } = await query
        .order("hospital_name", { ascending: true, nullsFirst: false });

      if (queryError) throw queryError;

      // Ordenar manualmente si hospital_name es null
      const sorted = (data || []).sort((a, b) => {
        const nameA = getOrganizationDisplayName(a);
        const nameB = getOrganizationDisplayName(b);
        return nameA.localeCompare(nameB);
      });

      setAssociations(sorted);
    } catch (err) {
      console.error("Error loading associations:", err);
      setError("Error al cargar las asociaciones");
      setAssociations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadAssociations(searchTerm);
  };

  const handleAssociationClick = (associationId) => {
    navigate(`/association/${associationId}`);
  };

  const getAssociationName = (association) => {
    return getOrganizationDisplayName(association);
  };

  if (!user || user.role !== "doctor") {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Button>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Buscar Asociaciones
              </h1>
              <p className="text-gray-600">
                Busca y explora asociaciones profesionales disponibles
              </p>
            </div>
            <Button
              onClick={() => navigate("/doctor/association-messages")}
              className="flex items-center gap-2 whitespace-nowrap"
            >
              <MessageSquare className="w-4 h-4" />
              Ver Mensajes
            </Button>
          </div>
        </div>

        {/* Búsqueda */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t("app.search.title", "Búsqueda")}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSearch} className="space-y-4">
              <div>
                <Label htmlFor="search">{t("app.search.searchAssociationLabel", "Buscar Asociación")}</Label>
                <div className="flex gap-2 mt-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      id="search"
                      type="text"
                      placeholder={t("app.search.searchAssociation", "Buscar por nombre de asociación...")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Button type="submit" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Buscando...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Buscar
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Resultados */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Spinner className="w-10 h-10" />
          </div>
        ) : associations.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">
                {searchTerm
                  ? "No se encontraron asociaciones con ese criterio de búsqueda"
                  : "No hay asociaciones disponibles"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {associations.map((association) => (
              <Card
                key={association.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleAssociationClick(association.id)}
              >
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    {association.picture ? (
                      <img
                        src={association.picture}
                        alt={getAssociationName(association)}
                        className="w-12 h-12 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-purple-200 rounded-lg flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-purple-600" />
                      </div>
                    )}
                    <CardTitle className="text-lg">
                      {getAssociationName(association)}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {association.email && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Mail className="w-4 h-4" />
                        <span className="truncate">{association.email}</span>
                      </div>
                    )}
                    {association.phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Phone className="w-4 h-4" />
                        <span>{association.phone}</span>
                      </div>
                    )}
                    {association.created_at && (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Clock className="w-4 h-4" />
                        <span>
                          Desde{" "}
                          {formatDateDMY(association.created_at)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 pt-4 border-t">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAssociationClick(association.id);
                      }}
                    >
                      Ver Detalles
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

